import { AxiosResponse } from 'axios';

import apiClient from '../client';
import { ApiResponseDto } from 'interfaces/apiResponse';
import { InvoiceDto, InvoiceGenerateRequestDto, InvoiceUpdateRequestDto } from 'interfaces/orders/invoice';
import { OrderBranchNoCompanyError, OrderStatusNotEligibleError } from './invoiceErrors';

export { OrderStatusNotEligibleError, OrderBranchNoCompanyError } from './invoiceErrors';

const throwKnownInvoiceError = (error: any): never => {
  const code = error.response?.data?.errors?.[0]?.code;
  if (code === 'ORDER_STATUS_NOT_ELIGIBLE') {
    throw new OrderStatusNotEligibleError();
  }
  if (code === 'ORDER_BRANCH_NO_COMPANY') {
    throw new OrderBranchNoCompanyError();
  }
  throw error;
};

interface InvoiceService {
  list(orderId: string): Promise<InvoiceDto[]>;
  generate(orderId: string, data?: InvoiceGenerateRequestDto): Promise<InvoiceDto>;
  update(orderId: string, invoiceId: string, data: InvoiceUpdateRequestDto): Promise<InvoiceDto>;
  get(orderId: string, invoiceId: string): Promise<InvoiceDto>;
  issue(orderId: string, invoiceId: string): Promise<InvoiceDto>;
  submit(orderId: string, invoiceId: string): Promise<InvoiceDto>;
  cancel(orderId: string, invoiceId: string): Promise<InvoiceDto>;
}

const endpoints = {
  list: (orderId: string) => `/orders/${orderId}/invoices`,
  generate: (orderId: string) => `/orders/${orderId}/invoices`,
  get: (orderId: string, invoiceId: string) => `/orders/${orderId}/invoices/${invoiceId}`,
  issue: (orderId: string, invoiceId: string) => `/orders/${orderId}/invoices/${invoiceId}/issue`,
  submit: (orderId: string, invoiceId: string) => `/orders/${orderId}/invoices/${invoiceId}/submit`,
  cancel: (orderId: string, invoiceId: string) => `/orders/${orderId}/invoices/${invoiceId}/cancel`,
};

const getErrorMessage = (response: ApiResponseDto<unknown>): string => response.errors!.map(error => error.message).join(', ');

export const invoicesService: InvoiceService = {
  list: async (orderId: string): Promise<InvoiceDto[]> => {
    const response: AxiosResponse<ApiResponseDto<InvoiceDto[]>> = await apiClient.get(endpoints.list(orderId));

    if (response.data.errors) {
      throw new Error(getErrorMessage(response.data));
    }

    return response.data.data!;
  },

  generate: async (orderId: string, data?: InvoiceGenerateRequestDto): Promise<InvoiceDto> => {
    try {
      const response: AxiosResponse<ApiResponseDto<InvoiceDto>> = await apiClient.post(endpoints.generate(orderId), data);

      if (response.data.errors) {
        throw new Error(getErrorMessage(response.data));
      }

      return response.data.data!;
    } catch (error: any) {
      return throwKnownInvoiceError(error);
    }
  },

  update: async (orderId: string, invoiceId: string, data: InvoiceUpdateRequestDto): Promise<InvoiceDto> => {
    try {
      const response: AxiosResponse<ApiResponseDto<InvoiceDto>> = await apiClient.patch(endpoints.get(orderId, invoiceId), data);

      if (response.data.errors) {
        throw new Error(getErrorMessage(response.data));
      }

      return response.data.data!;
    } catch (error: any) {
      return throwKnownInvoiceError(error);
    }
  },

  get: async (orderId: string, invoiceId: string): Promise<InvoiceDto> => {
    const response: AxiosResponse<ApiResponseDto<InvoiceDto>> = await apiClient.get(endpoints.get(orderId, invoiceId));

    if (response.data.errors) {
      throw new Error(getErrorMessage(response.data));
    }

    return response.data.data!;
  },

  issue: async (orderId: string, invoiceId: string): Promise<InvoiceDto> => {
    try {
      const response: AxiosResponse<ApiResponseDto<InvoiceDto>> = await apiClient.post(endpoints.issue(orderId, invoiceId));

      if (response.data.errors) {
        throw new Error(getErrorMessage(response.data));
      }

      return response.data.data!;
    } catch (error: any) {
      return throwKnownInvoiceError(error);
    }
  },

  submit: async (orderId: string, invoiceId: string): Promise<InvoiceDto> => {
    try {
      const response: AxiosResponse<ApiResponseDto<InvoiceDto>> = await apiClient.post(endpoints.submit(orderId, invoiceId));

      if (response.data.errors) {
        throw new Error(getErrorMessage(response.data));
      }

      return response.data.data!;
    } catch (error: any) {
      return throwKnownInvoiceError(error);
    }
  },

  cancel: async (orderId: string, invoiceId: string): Promise<InvoiceDto> => {
    const response: AxiosResponse<ApiResponseDto<InvoiceDto>> = await apiClient.post(endpoints.cancel(orderId, invoiceId));

    if (response.data.errors) {
      throw new Error(getErrorMessage(response.data));
    }

    return response.data.data!;
  },
};
